import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useInventoryPlanning } from '../contexts/InventoryPlanningContext';
import { useDatabaseContext } from '../contexts/DatabaseContext';
import OrderParamsModal from './OrderParamsModal';
import MonthlyPivotTable from './MonthlyPivotTable';
import api from '../services/api';
import { API_ROUTES } from '../config/api';

type Mode = 'supplier' | 'supplier-location';


const OrderPanel: React.FC = () => {
  const {
    data,
    supplierSelections,
    locationSelections,
    removeSupplierSelection,
    removeLocationSelection,
    clearSelections,
    setDraft,
    draft,
    allowNegativeStock,
    refresh,
    serviceLevel,
    coverageMonths,
  } = useInventoryPlanning();
  const { activeDatabase } = useDatabaseContext();
  const [mode, setMode] = useState<Mode>('supplier');

  const header = useMemo(() => {
    return mode === 'supplier'
      ? `Sélection – ${supplierSelections.length} fournisseurs`
      : `Sélection – ${locationSelections.length} lieux`;
  }, [mode, supplierSelections.length, locationSelections.length]);

  // Create supplier ID to name mapping for displaying supplier names in draft views
  const supplierNameMap = useMemo(() => {
    const map = new Map<number | string, string>();
    // First, add suppliers from supplierSelections (prioritize selected suppliers)
    for (const s of supplierSelections) {
      if (s.supplier_name) {
        map.set(s.supplier_id, s.supplier_name);
      }
    }
    // Then, add suppliers from data.suppliers if available (fallback)
    if (data?.suppliers) {
      for (const s of data.suppliers) {
        // Only add if not already in map (supplierSelections takes priority)
        if (!map.has(s.supplier_id) && s.supplier_name) {
          map.set(s.supplier_id, s.supplier_name);
        }
      }
    }
    return map;
  }, [supplierSelections, data?.suppliers]);

  const [paramsOpen, setParamsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cartTab, setCartTab] = useState<'selection' | 'draft' | 'history'>('selection');
  const [history, setHistory] = useState<any[]>([]);
  const [draftView, setDraftView] = useState<'articles' | 'containers' | 'plan12m'>('articles');
  const [expandedOrders, setExpandedOrders] = useState<Record<number, boolean>>({});
  const SHOW_PLAN_12M = false;
  const [panelWidth, setPanelWidth] = useState<number>(380);
  const [isResizing, setIsResizing] = useState(false);
  const isResizingRef = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(380);
  const [collapsed, setCollapsed] = useState(false);
  const STATUS_OPTIONS = ['pending', 'ordered', 'in_transit', 'delivered', 'cancelled'];
  const statusStyleClass = (s: string) => {
    switch (s) {
      case 'ordered':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'in_transit':
        return 'bg-amber-100 text-amber-800 border-amber-300';
      case 'delivered':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'pending':
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };
  const StatusSelect: React.FC<{ value: string; onChange: (v: string) => void; className?: string }>=({ value, onChange, className }) => (
    <select
      className={`border rounded px-2 py-1 text-xs appearance-none pr-6 hover:shadow-sm ${statusStyleClass(value)} ${className || ''}`}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
    >
      {STATUS_OPTIONS.map((s) => (
        <option key={s} value={s}>{s}</option>
      ))}
    </select>
  );

  // Debounced aggregate refresh after status changes in history
  const statusRefreshTimeoutRef = useRef<number | null>(null);
  const scheduleAggregateRefresh = useCallback(() => {
    if (statusRefreshTimeoutRef.current) {
      window.clearTimeout(statusRefreshTimeoutRef.current);
    }
    // Use a relatively long debounce to avoid thrashing the backend
    statusRefreshTimeoutRef.current = window.setTimeout(() => {
      refresh();
      statusRefreshTimeoutRef.current = null;
    }, 5000);
  }, [refresh]);
  useEffect(() => {
    return () => {
      if (statusRefreshTimeoutRef.current) {
        window.clearTimeout(statusRefreshTimeoutRef.current);
        statusRefreshTimeoutRef.current = null;
      }
    };
  }, []);

  const setOrderStatus = async (orderId: number, statusValue: string) => {
    try {
      const dbId = String(activeDatabase?.id || '');
      await api.patch(API_ROUTES.APPRO.BASE + `/orders/${dbId}/order/${orderId}/status/`, { status: statusValue });
      await refreshHistory();
      scheduleAggregateRefresh();
    } catch {}
  };
  const deleteOrder = async (orderId: number) => {
    try {
      if (!window.confirm('Supprimer définitivement cette commande ?')) return;
      const dbId = String(activeDatabase?.id || '');
      await api.delete(API_ROUTES.APPRO.BASE + API_ROUTES.APPRO.ORDERS.DELETE(dbId, orderId));
      await refreshHistory();
      scheduleAggregateRefresh();
    } catch {}
  };
  const setContainerStatus = async (containerId: number, statusValue: string) => {
    try {
      const dbId = String(activeDatabase?.id || '');
      await api.patch(API_ROUTES.APPRO.BASE + `/orders/${dbId}/container/${containerId}/status/`, { status: statusValue });
      await refreshHistory();
      scheduleAggregateRefresh();
    } catch {}
  };
  const setLineStatus = async (lineId: number, statusValue: string) => {
    try {
      const dbId = String(activeDatabase?.id || '');
      await api.patch(API_ROUTES.APPRO.BASE + `/orders/${dbId}/line/${lineId}/status/`, { status: statusValue });
      await refreshHistory();
      scheduleAggregateRefresh();
    } catch {}
  };

  // Helpers to compute container capacity, used volume and fill rate on the client
  const parseCapacityM3 = (containerType: any): number => {
    if (containerType == null) return 0;
    const s = String(containerType);
    // Extract leading number in forms like "65m3" or "65 m3"
    const m = s.match(/([0-9]+(?:\.[0-9]+)?)/);
    return m ? Number(m[1]) : Number(s) || 0;
  };

  const computeLineUnitVolume = (ln: any): number => {
    const uv = Number(ln?.unit_volume_m3);
    if (!Number.isNaN(uv) && uv > 0) return uv;
    const q = Number(ln?.quantity || 0);
    const v = Number(ln?.volume_m3 || 0);
    if (q > 0 && v > 0) return v / q;
    return 0;
  };

  const computeContainerStats = (container: any) => {
    const capacity = parseCapacityM3(container?.container_type);
    const lines = (container?.lines || []) as any[];
    let used = 0;
    for (const ln of lines) {
      const q = Math.max(0, Number(ln?.quantity || 0));
      const uv = computeLineUnitVolume(ln);
      used += q * uv;
    }
    const fill = capacity > 0 ? used / capacity : 0;
    return { capacity, used, fill };
  };

  const handleGenerate = async (params: { strategy: 'coverage' | 'ss_lt'; service_z: number; container_sizes_m3: Array<30 | 65>; min_fill_rate: number; rop_mode?: 'rop_ss' | 'rop_cb'; holding_cost_per_unit_month?: number; pull_forward_window_months?: number }) => {
    setParamsOpen(false);
    setLoading(true);
    try {
      const body: any = {
        mode: mode === 'supplier' ? 'supplier' : 'location',
        supplier_ids: supplierSelections.map((s) => s.supplier_id),
        location_groups: locationSelections.map((l) => l.location_group),
        params: { ...params, allow_negative_stock: allowNegativeStock, service_z: serviceLevel, coverage_months: coverageMonths },
      };
      // We need dbId; extract from existing data context where possible (onhand_today or suppliers may be empty). For now, require aggregate call first.
      // Call backend draft generator
      const dbId = String(activeDatabase?.id || '');
      const resp = await api.post(API_ROUTES.APPRO.BASE + `/orders/draft/${dbId}/generate/`, body);
      setDraft(resp.data);
      setCartTab('draft');
    } finally {
      setLoading(false);
    }
  };

  const refreshHistory = useCallback(async () => {
    try {
      const dbId = String(activeDatabase?.id || '');
      const resp = await api.get(API_ROUTES.APPRO.BASE + API_ROUTES.APPRO.ORDERS.LIST(dbId));
      setHistory(resp.data || []);
    } catch {}
  }, [activeDatabase]);

  // Ensure we load history when switching tabs
  useEffect(() => {
    if (cartTab === 'history') refreshHistory();
  }, [cartTab, refreshHistory]);

  const handleCommit = async () => {
    if (!draft) return;
    setLoading(true);
    try {
      const dbId = String(activeDatabase?.id || '');
      const body: any = {
        supplier_ids: supplierSelections.map((s) => s.supplier_id),
        location_groups: locationSelections.map((l) => l.location_group),
        params: { allow_negative_stock: allowNegativeStock },
        containers: draft.containers || [],
        lines: (draft.lines || []).map((l: any) => ({
          article_ref: l.article_supplier_article_ref,
          supplier_id: l.article_supplier_supplier_id,
          quantity: l.quantity || 0,
          unit_volume_m3: l.unit_volume_m3 || 0,
          location_group: l.location_group,
        })),
      };
      await api.post(API_ROUTES.APPRO.BASE + API_ROUTES.APPRO.ORDERS.COMMIT(dbId), body);
      setDraft(null);
      setCartTab('history');
      await refreshHistory();
    } finally {
      setLoading(false);
    }
  };

  // Resizer handlers
  const onWindowMouseMove = useCallback((ev: MouseEvent) => {
    if (!isResizingRef.current) return;
    const delta = startXRef.current - ev.clientX;
    const desired = startWidthRef.current + delta;
    // Prevent overflowing the main content by reserving a minimum main width
    // Allow left pane to shrink and handle its own horizontal scroll; keep a small rail visible
    const MIN_LEFT_RAIL = 48; // leave space so left pane can show/use its scrollbar
    const MAX_WIDTH = Math.max(300, window.innerWidth - MIN_LEFT_RAIL);
    const clamped = Math.min(Math.max(300, desired), MAX_WIDTH);
    setPanelWidth(clamped);
  }, []);
  const onWindowMouseUp = useCallback(() => {
    if (!isResizingRef.current) return;
    isResizingRef.current = false;
    setIsResizing(false);
    window.removeEventListener('mousemove', onWindowMouseMove);
    window.removeEventListener('mouseup', onWindowMouseUp);
  }, [onWindowMouseMove]);
  const onMouseDownResize = (e: React.MouseEvent<HTMLDivElement>) => {
    isResizingRef.current = true;
    setIsResizing(true);
    startXRef.current = e.clientX;
    startWidthRef.current = panelWidth;
    window.addEventListener('mousemove', onWindowMouseMove);
    window.addEventListener('mouseup', onWindowMouseUp);
    e.preventDefault();
  };
  useEffect(() => {
    return () => {
      window.removeEventListener('mousemove', onWindowMouseMove);
      window.removeEventListener('mouseup', onWindowMouseUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onWindowMouseMove, onWindowMouseUp]);

  // Local sync of quantities etc. (filter is now handled in context)

  return (
    <div
      className={`bg-white flex-shrink-0 h-full flex flex-col relative ${collapsed ? '' : 'border-l'}`}
      style={{ maxWidth: '100%', width: collapsed ? 12 : panelWidth, transition: isResizing ? 'none' : 'width 150ms ease', overflow: 'hidden', userSelect: isResizing ? 'none' : 'auto' }}
      
    >
      {!collapsed && (
        <div className="border-b border-gray-200">
          <ul className="flex flex-wrap text-sm font-medium text-center text-gray-500">
            <li className="me-2">
              <button
                className={`inline-block px-4 py-2 rounded-t-lg ${cartTab !== 'history' ? 'text-blue-600 bg-gray-100' : 'hover:text-gray-600 hover:bg-gray-50'}`}
                aria-current={cartTab !== 'history' ? 'page' : undefined}
                onClick={() => setCartTab(draft ? 'draft' : 'selection')}
              >
                Commande
              </button>
            </li>
            <li className="me-2">
              <button
                className={`inline-block px-4 py-2 rounded-t-lg ${cartTab === 'history' ? 'text-blue-600 bg-gray-100' : 'hover:text-gray-600 hover:bg-gray-50'}`}
                onClick={() => { setCartTab('history'); refreshHistory(); }}
              >
                Historique
              </button>
            </li>
          </ul>
        </div>
      )}


      {/* Top-level tabs: Commande / Historique */}
      
      {/* Resize / expand handle */}
      <div
        onMouseDown={!collapsed ? onMouseDownResize : undefined}
        onClick={collapsed ? () => setCollapsed(false) : undefined}
        className={`absolute left-0 top-0 h-full ${collapsed ? 'w-12' : 'w-3'} ${collapsed ? 'cursor-pointer' : 'cursor-col-resize'} bg-transparent hover:bg-gray-200 flex items-center justify-center`}
        style={{ transform: 'none' }}
        title={collapsed ? 'Déplier' : 'Redimensionner'}
      >
        {collapsed && <span className="text-xs text-gray-600">◀</span>}
      </div>
      {!collapsed && cartTab !== 'history' && (
      <div className="p-4 border-b font-medium space-y-2">
        <div className="flex items-center justify-between">
          <span>{header}</span>
            <div className="flex items-center gap-2">
              <div className="inline-flex rounded border overflow-hidden">
                <button className={`px-3 py-1 text-sm ${cartTab === 'selection' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700'}`} onClick={() => setCartTab('selection')}>Sélection</button>
                <button className={`px-3 py-1 text-sm border-l ${cartTab === 'draft' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700'}`} onClick={() => setCartTab('draft')}>Commande</button>
              </div>
              <button
                className={`px-2 py-1 text-xs rounded border ${collapsed ? 'bg-white text-gray-700' : 'bg-white text-gray-700'}`}
                onClick={() => setCollapsed((c) => !c)}
                title={collapsed ? 'Déplier' : 'Replier'}
              >
                {collapsed ? '▶' : '◀'}
              </button>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {cartTab === 'selection' ? (
              <div className="inline-flex rounded border overflow-hidden">
                <button className={`px-3 py-1 text-sm ${mode === 'supplier' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`} onClick={() => setMode('supplier')}>Fournisseurs</button>
                <button className={`px-3 py-1 text-sm border-l ${mode === 'supplier-location' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`} onClick={() => setMode('supplier-location')}>Lieux</button>
              </div>
            ) : (
              <div className="inline-flex rounded border overflow-hidden">
                <button className={`px-3 py-1 text-sm ${draftView === 'articles' ? 'bg-green-600 text-white' : 'bg-white text-gray-700'}`} onClick={() => setDraftView('articles')}>Articles</button>
                <button className={`px-3 py-1 text-sm border-l ${draftView === 'containers' ? 'bg-green-600 text-white' : 'bg-white text-gray-700'}`} onClick={() => setDraftView('containers')}>Conteneurs</button>
                  {SHOW_PLAN_12M && (
                <button className={`px-3 py-1 text-sm border-l ${draftView === 'plan12m' ? 'bg-green-600 text-white' : 'bg-white text-gray-700'}`} onClick={() => setDraftView('plan12m')}>Plan 12m</button>
                  )}
              </div>
            )}
              
          </div>
          
      </div>
      )}
      {!collapsed && (
      <div className="p-4 text-sm text-gray-600 flex-1 min-h-0 overflow-hidden">
        {cartTab === 'selection' && (
          <>
            {mode === 'supplier' ? (
              <>
                {supplierSelections.length === 0 ? (
                  <div className="text-gray-500">Aucun fournisseur sélectionné</div>
                ) : (
                  <div className="space-y-1">
                    {supplierSelections.map((s) => (
                      <div key={s.supplier_id} className="border rounded p-2 flex items-center justify-between">
                        <div className="font-medium text-gray-900">{s.supplier_name || `Fournisseur ${s.supplier_id}`}</div>
                        <button className="text-red-600 text-xs" onClick={() => removeSupplierSelection(s.supplier_id)}>Retirer</button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <>
                {locationSelections.length === 0 ? (
                  <div className="text-gray-500">Aucun lieu sélectionné</div>
                ) : (
                  <div className="space-y-1">
                    {locationSelections.map((l) => (
                      <div key={l.location_group} className="border rounded p-2 flex items-center justify-between">
                        <div className="font-medium text-gray-900">{l.location_group}</div>
                        <button className="text-red-600 text-xs" onClick={() => removeLocationSelection(l.location_group)}>Retirer</button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        )}

        {cartTab === 'history' && (
          <div className="space-y-3 h-full flex flex-col" id="history-root">
            <div className="text-sm text-gray-600">{history.length} commande{history.length === 1 ? '' : 's'} au total</div>
            <div className="flex-1 min-h-0 overflow-auto border rounded">
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 text-gray-700">
                    <th className="px-2 py-2 text-left">ID</th>
                    <th className="px-2 py-2 text-left">Créée</th>
                    <th className="px-2 py-2 text-right">Lignes</th>
                    <th className="px-2 py-2 text-right">Qté</th>
                    <th className="px-2 py-2 text-right">Volume (m³)</th>
                    <th className="px-2 py-2 text-right">Conteneurs</th>
                    <th className="px-2 py-2 text-left">Statut</th>
                    <th className="px-2 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((o: any, idx: number) => (
                    <React.Fragment key={o.id}>
                      <tr className="border-t align-top">
                        <td className="px-2 py-2">{o.id}<span className="sr-only">row-{idx}</span></td>
                        <td className="px-2 py-2">{String(o.created_at || '').slice(0, 19).replace('T',' ')}</td>
                        <td className="px-2 py-2 text-right">{o.total_lines}</td>
                        <td className="px-2 py-2 text-right">{o.total_quantity}</td>
                        <td className="px-2 py-2 text-right">{Number(o.total_volume_m3 || 0).toFixed(2)}</td>
                        <td className="px-2 py-2 text-right">{o.container_count}</td>
                        <td className="px-2 py-2 text-left">
                          <StatusSelect value={o.status || 'pending'} onChange={(v) => setOrderStatus(o.id, v)} />
                        </td>
                        <td className="px-2 py-2 text-right space-x-2">
                          <button
                            className="px-2 py-1 text-xs rounded border bg-white text-gray-700 hover:bg-gray-50"
                            onClick={() => setExpandedOrders((prev) => ({ ...prev, [o.id]: !prev[o.id] }))}
                          >
                            {expandedOrders[o.id] ? 'Masquer' : 'Voir détails'}
                          </button>
                          <button
                            className="px-2 py-1 text-xs rounded border bg-red-50 text-red-700 hover:bg-red-100"
                            onClick={() => deleteOrder(o.id)}
                          >
                            Supprimer
                          </button>
                        </td>
                      </tr>
                      {expandedOrders[o.id] && (
                        <tr className="border-t">
                          <td colSpan={8} className="px-2 py-2 bg-gray-50">
                            {(() => {
                              const byContainer: Record<string, any[]> = {};
                              for (const ln of (o.lines || [])) {
                                const cid = String(ln.container_id || '');
                                (byContainer[cid] ||= []).push(ln);
                              }
                              const containerMap: Record<string, any> = {};
                              for (const c of (o.containers || [])) containerMap[String(c.id)] = c;
                              const keysSet = new Set<string>([...Object.keys(byContainer), ...Object.keys(containerMap)]);
                              const keys = Array.from(keysSet).sort((a, b) => {
                                const na = a === '' ? -1 : (parseInt(a, 10) || 0);
                                const nb = b === '' ? -1 : (parseInt(b, 10) || 0);
                                return na - nb;
                              });
                              if (keys.length === 0) return <div className="text-xs text-gray-500">Aucune ligne</div>;
                              return (
                                <div className="space-y-2">
                                  {keys.map((cid) => {
                                    const c = containerMap[cid];
                                    // Compute fill rate: prefer API value; if 0 (legacy bug), derive from capacity/used or from lines
                                    const apiFillRate = Number(c?.fill_rate || 0);
                                    let displayFillRate = apiFillRate;
                                    if (displayFillRate <= 0 && c) {
                                      const cap = c.capacity_m3 > 0 ? Number(c.capacity_m3) : parseCapacityM3(c.container_type);
                                      const used = c.used_volume_m3 > 0 ? Number(c.used_volume_m3) : 0;
                                      if (cap > 0 && used > 0) {
                                        displayFillRate = used / cap;
                                      } else if (cap > 0 && (byContainer[cid] || []).length > 0) {
                                        const synth = { container_type: c.container_type, lines: byContainer[cid] };
                                        const { fill } = computeContainerStats(synth);
                                        displayFillRate = fill;
                                      }
                                    }
                                    return (
                                      <div key={`group-${o.id}-${cid}`} className="border rounded bg-white">
                                        <div className="px-2 py-1 text-xs font-semibold text-gray-700 border-b flex items-center justify-between">
                                          <div className="flex items-center gap-2">
                                            {cid && c ? (
                                              <>
                                                <span>
                                                  #{c.id} · {c.location_group} · {c.container_type} · {(displayFillRate * 100).toFixed(0)}%
                                                  {' '}· créée: {String(c.created_at || '').slice(0, 19).replace('T',' ')}
                                                  {' '}· échéance: {String(c.expected_delivery_date || '')}
                                                </span>
                                              </>
                                            ) : (
                                              <span>Sans conteneur</span>
                                            )}
                                          </div>
                                          <div>
                                            {cid && c ? (
                                              <StatusSelect value={c.status || 'pending'} onChange={(v) => setContainerStatus(c.id, v)} className="bg-white" />
                                            ) : null}
                                          </div>
                                        </div>
                                        <div className="p-2">
                                          <table className="min-w-full text-xs">
                                            <thead>
                                              <tr className="text-gray-600">
                                                <th className="px-2 py-1 text-left">Article</th>
                                                <th className="px-2 py-1 text-left">Fournisseur</th>
                                                <th className="px-2 py-1 text-right">Qté</th>
                                                <th className="px-2 py-1 text-right">Volume</th>
                                                <th className="px-2 py-1 text-left">ETA</th>
                                                <th className="px-2 py-1 text-left">Statut</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {(byContainer[cid] || []).length === 0 ? (
                                                <tr><td colSpan={6} className="px-2 py-2 text-gray-500">Aucune ligne</td></tr>
                                              ) : (
                                                (byContainer[cid] || []).map((ln: any, i: number) => (
                                                  <tr key={`ln-${i}`} className="border-t">
                                                    <td className="px-2 py-1">{ln.article_ref}</td>
                                                    <td className="px-2 py-1">{ln.supplier_name || ln.supplier_id}</td>
                                                    <td className="px-2 py-1 text-right">{ln.quantity}</td>
                                                    <td className="px-2 py-1 text-right">{Number(ln.volume_m3 || (ln.unit_volume_m3 || 0) * (ln.quantity || 0)).toFixed(2)}</td>
                                                    <td className="px-2 py-1">{String(ln.expected_delivery_date || '')}</td>
                                                    <td className="px-2 py-1">
                                                      <StatusSelect value={ln.status || 'pending'} onChange={(v) => setLineStatus(ln.id, v)} />
                                                    </td>
                                                  </tr>
                                                ))
                                              )}
                                            </tbody>
                                          </table>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              );
                            })()}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {cartTab === 'draft' && (
          <>
            {!draft ? (
              <div className="text-gray-500">Aucune commande générée pour le moment.</div>
            ) : draftView === 'articles' ? (
              <div className="space-y-3">
                {(() => {
                  // Build aggregated rows by (location_group, supplier_id, article_ref)
                  const rows: Array<{ key: string; location_group: string; supplier_id: number | string; supplier_name?: string; article_ref: string; quantity: number }>=[];
                  const index: Record<string, number> = {};
                  const addRow = (loc: string, sup: any, ref: string, qty: number) => {
                    const k = `${loc}::${sup}::${ref}`;
                    const pos = index[k];
                    if (pos === undefined) {
                      const supplierName = supplierNameMap.get(sup) || undefined;
                      index[k] = rows.length;
                      rows.push({ key: k, location_group: loc, supplier_id: sup, supplier_name: supplierName, article_ref: ref, quantity: Math.max(0, Number(qty || 0)) });
                    } else {
                      rows[pos].quantity += Math.max(0, Number(qty || 0));
                    }
                  };
                  // Prefer containers when present to reflect real commit payload
                  if (draft?.containers && draft.containers.length > 0) {
                    for (const c of draft.containers as any[]) {
                      const loc = String(c?.location_group || '');
                      for (const ln of (c?.lines || []) as any[]) {
                        addRow(loc, ln?.supplier_id, String(ln?.article_ref || ''), Number(ln?.quantity || 0));
                      }
                    }
                  } else {
                    for (const l of (draft.lines || []) as any[]) {
                      addRow(String(l?.location_group || ''), l?.article_supplier_supplier_id, String(l?.article_supplier_article_ref || ''), Number(l?.quantity || 0));
                    }
                  }
                  if (rows.length === 0) return <div className="text-gray-500">Aucune ligne</div>;
                  // Sort for stability
                  rows.sort((a, b) => (
                    String(a.location_group).localeCompare(String(b.location_group)) ||
                    String(a.supplier_id).localeCompare(String(b.supplier_id)) ||
                    String(a.article_ref).localeCompare(String(b.article_ref))
                  ));
                  const onChangeQty = (row: typeof rows[number], newQty: number) => {
                    const d = { ...(draft || {}), containers: [...(draft?.containers || [])], lines: [...(draft?.lines || [])] } as any;
                    const loc = row.location_group;
                    const sup = row.supplier_id;
                    const ref = row.article_ref;
                    if (d.containers && d.containers.length > 0) {
                      // redistribute to first matching container line; zero out the rest
                      let remaining = Math.max(0, Math.floor(newQty));
                      for (let ci = 0; ci < d.containers.length; ci++) {
                        const c = d.containers[ci];
                        const lines = [...(c.lines || [])];
                        for (let li = 0; li < lines.length; li++) {
                          const ln = lines[li];
                          if (String(c.location_group || '') === String(loc) && String(ln.supplier_id || '') === String(sup) && String(ln.article_ref || '') === String(ref)) {
                            const assign = Math.max(0, remaining);
                            lines[li] = { ...ln, quantity: assign };
                            remaining = 0;
                          }
                        }
                        d.containers[ci] = { ...c, lines };
                      }
                      // If nothing matched (shouldn't happen), no-op
                    } else {
                      // Update or insert in top-level lines
                      let matched = false;
                      for (let i = 0; i < d.lines.length; i++) {
                        const l = d.lines[i];
                        if (String(l.location_group || '') === String(loc) && String(l.article_supplier_supplier_id || '') === String(sup) && String(l.article_supplier_article_ref || '') === String(ref)) {
                          d.lines[i] = { ...l, quantity: Math.max(0, Math.floor(newQty)) };
                          matched = true;
                          break;
                        }
                      }
                      if (!matched) {
                        d.lines.push({ location_group: loc, article_supplier_supplier_id: sup, article_supplier_article_ref: ref, quantity: Math.max(0, Math.floor(newQty)) });
                      }
                    }
                    setDraft(d);
                  };
                  return (
                  <div className="max-h-96 overflow-auto border rounded">
                    <table className="min-w-full text-xs">
                      <thead>
                        <tr className="bg-gray-50 text-gray-700">
                          <th className="px-2 py-2 text-left">Article</th>
                            <th className="px-2 py-2 text-left">Lieu</th>
                            <th className="px-2 py-2 text-left">Fournisseur</th>
                            
                            <th className="px-2 py-2 text-right">Qté</th>
                        </tr>
                      </thead>
                      <tbody>
                          {rows.map((r) => (
                            <tr key={r.key} className="border-t">
                              <td className="px-2 py-2">{r.article_ref}</td>
                              <td className="px-2 py-2">{r.location_group}</td>
                              <td className="px-2 py-2">{r.supplier_name || r.supplier_id}</td>
                              
                              <td className="px-2 py-2 text-right">
                                <input
                                  type="number"
                                  className="w-24 border rounded px-1 py-0.5 text-right"
                                  value={Number(r.quantity || 0)}
                                  onChange={(e) => {
                                    const val = e.target.value === '' ? 0 : Number(e.target.value);
                                    onChangeQty(r, val);
                                  }}
                                />
                              </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  );
                })()}
              </div>
            ) : draftView === 'containers' ? (
              <div className="space-y-3">
                {(!draft.containers || draft.containers.length === 0) ? (
                  <div className="text-gray-500">Aucun conteneur généré</div>
                ) : (
                  <div className="max-h-96 overflow-auto border rounded">
                    <table className="min-w-full text-xs">
                      <thead>
                        <tr className="bg-gray-50 text-gray-700">
                          <th className="px-2 py-2 text-left">Lieu</th>
                          <th className="px-2 py-2 text-left">Type</th>
                          <th className="px-2 py-2 text-right">Volume (m³)</th>
                          <th className="px-2 py-2 text-right">Taux</th>
                          <th className="px-2 py-2 text-left">Lignes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {draft.containers.map((c: any, idx: number) => {
                          const { used, fill } = computeContainerStats(c);
                          return (
                          <tr key={idx} className="border-t align-top">
                            <td className="px-2 py-2">{c.location_group}</td>
                            <td className="px-2 py-2">{c.container_type}</td>
                            <td className="px-2 py-2 text-right">{used.toFixed(2)}</td>
                            <td className="px-2 py-2 text-right">{(fill * 100).toFixed(0)}%</td>
                            <td className="px-2 py-2">
                              <div className="space-y-1">
                                {(c.lines || []).map((ln: any, i: number) => {
                                  const supplierName = supplierNameMap.get(ln?.supplier_id) || ln?.supplier_id;
                                  return (
                                  <div key={i} className="flex items-center justify-between gap-2">
                                    <span className="truncate">{ln.article_ref} · {supplierName}</span>
                                    <input
                                      type="number"
                                      className="w-20 border rounded px-1 py-0.5 text-right"
                                      value={ln.quantity ?? 0}
                                      onChange={(e) => {
                                        const q = e.target.value === '' ? 0 : Number(e.target.value);
                                        // update quantity in draft
                                        const d = { ...(draft || {}), containers: [...(draft?.containers || [])] } as any;
                                        d.containers[idx] = { ...d.containers[idx], lines: [...(d.containers[idx].lines || [])] };
                                        d.containers[idx].lines[i] = { ...d.containers[idx].lines[i], quantity: q };
                                        setDraft(d);
                                      }}
                                    />
                                    <button
                                      className="text-red-600 text-xs"
                                      onClick={() => {
                                        const d = { ...(draft || {}), containers: [...(draft?.containers || [])] } as any;
                                        const newLines = (d.containers[idx].lines || []).slice();
                                        newLines.splice(i, 1);
                                        d.containers[idx] = { ...d.containers[idx], lines: newLines };
                                        setDraft(d);
                                      }}
                                    >
                                      Retirer
                                    </button>
                                  </div>
                                  );
                                })}
                              </div>
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ) : cartTab === 'draft' ? (
              <div className="space-y-3">
                {(!draft.plan_12m || draft.plan_12m.length === 0) ? (
                  <div className="text-gray-500">Aucun plan 12 mois généré</div>
                ) : (
                  <div className="max-h-96 overflow-auto">
                    <MonthlyPivotTable
                      rows={draft.plan_12m}
                      idField="sku_id"
                      dateField="month_index"
                      valueField="orders_to_place"
                      idHeader="Article"
                      baseMonth={data?.as_of_date?.slice(0, 7)}
                      valueFormatter={(v) => Number(v || 0).toFixed(0)}
                    />
                  </div>
                )}
              </div>
            ) : (
              <div />
            )}
          </>
        )}
      </div>
      )}
      {cartTab !== 'history' && (
      <div className="mt-auto p-4 border-t flex gap-2">
          {cartTab === 'draft' && (
            <button className="px-3 py-2 bg-green-600 text-white rounded" disabled={loading || !draft} onClick={handleCommit}>
              Enregistrer la commande
            </button>
          )}
        <button className="px-3 py-2 bg-blue-600 text-white rounded" disabled={loading} onClick={() => setParamsOpen(true)}>
          {loading
            ? 'Génération…'
            : (cartTab === 'draft'
                ? 'Régénérer'
                : (mode === 'supplier'
                    ? 'Générer une commande pour ces fournisseurs'
                    : 'Générer une commande pour ces zones de chargement'))}
        </button>
          {cartTab === 'selection' && (
        <button className="ml-auto px-3 py-2 bg-gray-50 text-gray-700 rounded" onClick={clearSelections}>Vider</button>
          )}
      </div>
      )}
      <OrderParamsModal open={paramsOpen} onClose={() => setParamsOpen(false)} onConfirm={handleGenerate} />
    </div>
  );
};

export default OrderPanel;



